import { Formik } from "formik";
import { Divider, Grid, TextField, FormLabel, Typography, IconButton, InputAdornment } from "@mui/material";
import { useEffect, useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Modal } from "../../../components/common/Modal/Modal";
import { ModalHeader } from "../../../components/common/Modal/ModalHeader";
import { ModalBody } from "../../../components/common/Modal/ModalBody";
import { ModalFooter } from "../../../components/common/Modal/ModalFooter";
import { useUserStore } from "../../../hooks/UseUserStore";
import { readLocalStorage } from "../../../toolbox/helpers/local-storage-helpers";
import { LocalStorageKey } from "../../../types";
import { UserERPAPI } from "../../../apis/UserERPApi";
import { useUserErpStore } from "../../../hooks/useUserErpStore";

type Values = {
    id: number,
    rut: string,
    username: string,
    password: string,
};

export const ModalIntegration: React.FC<any> = (props): JSX.Element | any => {
    const { open, closeModal, userSelected, listUser, clearState } = props;

    const { action } = useUserStore();
    const { getVerifyERP } = useUserErpStore();
    const userData = readLocalStorage(LocalStorageKey.USER_DATA);

    const [loading, setLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [message, setMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const [data, setData] = useState<Values>({
        id: userSelected ? userSelected.id : '',
        rut: '',
        username: '',
        password: '',
    });

    useEffect(() => {
        if (userSelected) {
            verifyDataInitial();
        }
    }, [userSelected]);

    const verifyDataInitial = async () => {
        const response = await getVerifyERP(userSelected?.id);
        if (!response) {
            setIsEdit(false);
            setMessage("No existe registro previo. Puede crear un nuevo usuario.");
            setData({
                ...data,
                rut: '',
                username: '',
                password: '',
            });
        } else {
            setIsEdit(true);
            setMessage("Ya existe un registro vinculado. Puede editar los datos o actualizar la contraseña.");
            setData({
                ...data,
                rut: response?.rut,
                username: response?.user,
                password: '', // Se deja vacío para forzar actualización si es necesario
            });
        }
    };

    const validateForm = (values) => {
        let errors: any = {};
        if (!values.username) errors.username = "El campo es requerido";
        if (!values.rut) errors.rut = "El campo es requerido";
        if (!values.password) errors.password = "El campo es requerido";
        return errors;
    };

    const onSubmit = async (values) => {
        setLoading(true);

        const formdata = {
            iduser: userSelected.id,
            rut: values.rut,
            user: values.username,
            password: values.password,
        };

        const response = isEdit
            ? await UserERPAPI.editUserERP(values.id, formdata)
            : await UserERPAPI.createUserERP(formdata);

        if (response) {
            listUser && listUser();
            closeModal && closeModal();
            clearState && clearState();
        }
        setLoading(false);
    };

    return (
        <Modal
            open={open}
            handleClose={closeModal}
            disableEscapeKeyDown
            disableBackdropClick
            size="lg"
            paperprops={{
                style: {
                    position: 'absolute',
                    right: -30,
                    borderRadius: 20,
                    animation: 'slide-in 0.3s ease-out',
                    maxHeight: '100%',
                    height: '100vh',
                },
            }}
        >
            <ModalHeader
                text={"Integrar con ERP"}
                className="positionElements"
                onCancel={closeModal}
                clearState={clearState}
            />
            <ModalBody>
                <Typography variant="body1" gutterBottom>{message}</Typography>
                <Formik
                    initialValues={data}
                    enableReinitialize
                    validate={(values) => validateForm(values)}
                    onSubmit={onSubmit}
                >
                    {({ values, errors, touched, handleSubmit, handleChange }) => (
                        <form onSubmit={handleSubmit}>
                            <Grid container sx={{ padding: '20px' }} spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <FormLabel>Username:</FormLabel>
                                    <TextField
                                        id="username"
                                        type="text"
                                        name="username"
                                        fullWidth
                                        size="small"
                                        value={values.username}
                                        onChange={handleChange}
                                        error={!!(errors.username && touched.username)}
                                        helperText={errors.username && touched.username ? errors.username : ''}
                                        inputProps={{ readOnly: isEdit }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormLabel>Rut:</FormLabel>
                                    <TextField
                                        id="rut"
                                        type="text"
                                        name="rut"
                                        fullWidth
                                        size="small"
                                        value={values.rut}
                                        onChange={handleChange}
                                        error={!!(errors.rut && touched.rut)}
                                        helperText={errors.rut && touched.rut ? errors.rut : ''}
                                        inputProps={{ readOnly: isEdit }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormLabel>Contraseña:</FormLabel>
                                    <TextField
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        fullWidth
                                        size="small"
                                        value={values.password}
                                        onChange={handleChange}
                                        error={!!(errors.password && touched.password)}
                                        helperText={errors.password && touched.password ? errors.password : ''}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Divider />
                            <ModalFooter
                                buttonType="submit"
                                confirmText={isEdit ? 'Actualizar' : 'Crear'}
                                onConfirm={handleSubmit}
                                cancelText={"Cancelar"}
                                onCancel={closeModal}
                                loadingConfirmText={loading}
                                clearState={clearState}
                            />
                        </form>
                    )}
                </Formik>
            </ModalBody>
        </Modal>
    );
};
